import { LitElement, html } from 'lit';

import tickerObserver from '../../utils/ticker/ticker-observer';
import translate from '../../utils/webcomponents/translate';
import constructDialog from '../../utils/webcomponents/construct-dialog';

import watchlistEvents from './watchlist-events';
import alertEvents from '../alert/alert-events';

import Dropdown from '../dropdown';
import Tooltip from '../tooltip';

import '../broker-dropdown';
import '../icon';

const STOPLOSS_TOGGLE_NAME = 'stoplossAlert';

const renderNameAndIsin = (productName, isin, showIsinColumn) =>
	showIsinColumn
		? html`${productName}`
		: html`
				${productName}
				<br />
				<span class="manageable-list__item-small">${isin}</span>
			`;

const renderActionButton = (label, icon, clickCallback) => html`
	<div class="manageable-list__item-button">
		<button
			class="button button--ghost button--icon-only button--small"
			@click="${clickCallback}"
			data-option-tooltip-text="${label}"
		>
			<span class="button__icon">
				<x-icon icon="${icon}"></x-icon>
			</span>
		</button>
	</div>
`;

const renderBrokerDropdown = (isin) => {
	const labelBuy = translate('watchlist', 'buy', 'Buy');

	return html`
		<div
			class="manageable-list__item-button"
			data-option-tooltip-text="${labelBuy}"
		>
			<x-broker-dropdown
				class="broker-dropdown"
				id="${isin}"
				.isin="${isin}"
			></x-broker-dropdown>
		</div>
	`;
};

const renderDropdownItem = (icon, clickCallback, label) => html`
	<button
		class="dropdown__item dropdown__item--padded has-js"
		@click="${clickCallback}"
	>
		<div class="dropdown__visual">
			<span class="button__icon">
				<x-icon icon="${icon}"></x-icon>
			</span>
		</div>
		<span class="dropdown__label">${label}</span>
	</button>
`;

const renderStoploss = (data) => {
	const labelStoplossAlert = translate('watchlist', 'stoplossAlert', 'Stop loss alert');

	return html`
		<div class="dropdown__item dropdown__item--padded has-js">
			<div class="dropdown__visual">
				<x-toggle
					id="${data.isin}"
					name="${STOPLOSS_TOGGLE_NAME}"
					value="${data.isin}"
					iconOn="alert"
					iconOff="alert-disabled"
					?isChecked="${data.hasStopLossLevelReachedNotification}"
					?isDisabled=${data.isProductInactive}
				>
				</x-toggle>
			</div>
			<label
				class="dropdown__label"
				for="toggle-${data.isin}"
			>
				${labelStoplossAlert}
			</label>
		</div>
	`;
};

const renderCopyIsin = (isin) => html`
	<div class="manageable-list__item-copy-isin">
		${isin}
		<x-copy-value-button
			.value="${isin}"
			.labelAction="${translate('watchlist', 'copy', 'Copy')}"
			.labelSuccess="${translate('watchlist', 'copied', 'Copied')}"
		></x-copy-value-button>
	</div>
`;

class WatchlistItem extends LitElement {
	static get properties() {
		return {
			data: { type: Object },
			showIsinColumn: { type: Boolean },
		};
	}

	constructor() {
		super();

		this.showIsinColumn = false;

		this.onChange = this.onChange.bind(this);
	}

	firstUpdated() {
		super.firstUpdated();

		// trick to make bid/ask unresponsive because the bid/ask
		// gets updated by `ticker-element`. When the element get's updated
		// by `ticker-element` first and later by `portfolio`, lit-element trows an error
		this.bid = this.data.bid;
		this.ask = this.data.ask;

		tickerObserver.addTickers(this);

		this.addEventListener('change', this.onChange);

		const dropdown = this.querySelector('.js-actions');

		if (dropdown) {
			this.dropdown = new Dropdown(dropdown).start();
		}

		this.createTooltips();

		this.requestUpdate();
	}

	disconnectedCallback() {
		if (this.isDragging) {
			return;
		}

		tickerObserver.removeTickers(this);

		super.disconnectedCallback();
	}

	onChange({ target }) {
		const { name, checked } = target;

		if (name === STOPLOSS_TOGGLE_NAME) {
			this.updateStopLossAlert(checked);
		}
	}

	render() {
		const { data } = this;

		const labelDelete = translate('watchlist', 'delete', 'Delete');

		const exchangeLabelMarkup = data.showExchangeIcon
			? html`&nbsp;<span class="tag">${data.exchangeName}</span>`
			: '';

		return html`
			<div class="table__cell manageable-list__item-action text-center">
				${data.isProductInactive
					? renderActionButton(labelDelete, 'delete', this.removeItem)
					: this.renderActions(data)}
			</div>

			${this.showIsinColumn ? html`<div class="table__cell">${renderCopyIsin(data.isin)}</div>` : ''}

			<div class="table__cell">
				<a
					class="manageable-list__item-link"
					href="${data.url}"
				>
					${renderNameAndIsin(data.productName, data.isin, this.showIsinColumn)}${exchangeLabelMarkup}
				</a>
			</div>

			<div class="table__cell text-right">${data.stopLossFormatted}</div>

			<div class="table__cell text-right">
				<span
					class="quote"
					data-isin="${data.isin}"
					data-feed-property="Bid"
					data-has-indicator="true"
				>
					${data.bidFormatted}
				</span>
			</div>
			<div class="table__cell text-right">
				<span
					class="quote"
					data-isin="${data.isin}"
					data-feed-property="Ask"
					data-has-indicator="true"
				>
					${data.askFormatted}
				</span>
			</div>
			<div class="table__cell text-right">
				<span
					class="quote"
					data-isin="${data.isin}"
					data-feed-property="PerformancePercentage"
					data-has-indicator="true"
				>
					${data.dailyPerformance}
				</span>
			</div>
		`;
	}

	renderActions(data) {
		const labelAddToPortfolio = translate('watchlist', 'addToPortfolio', 'Add to portfolio');
		const labelAddAlert = translate('watchlist', 'addAlert', 'Add an alert');
		const labelDelete = translate('watchlist', 'delete', 'Delete');

		return html`
			<button
				class="button button--ghost button--icon-only button--small button--drag"
				aria-controls="drag"
			>
				<x-icon icon="drag"></x-icon>
			</button>

			<div
				class="dropdown js-actions"
				data-option-placement="right"
			>
				<button
					class="button button--ghost button--icon-only button--small"
					aria-controls="menu-actions"
				>
					<x-icon icon="menu-kebab"></x-icon>
				</button>

				<div
					class="dropdown__list"
					id="menu-actions"
					role="menu"
				>
					${renderDropdownItem('alert', this.addAlert, labelAddAlert)} ${renderStoploss(data)}
					${renderDropdownItem('delete', this.removeItem, labelDelete)}
				</div>
			</div>

			${renderActionButton(labelAddToPortfolio, 'portfolio', this.addToPortfolio)}
			${renderBrokerDropdown(data.isin)}
		`;
	}

	createTooltips() {
		const tooltips = Array.from(this.querySelectorAll('[data-option-tooltip-text]'));

		tooltips.forEach((tooltip) => new Tooltip(tooltip).start());
	}

	removeItem() {
		this.dispatchCustomEvent(watchlistEvents.ITEM_REMOVE, this.data);
	}

	updateStopLossAlert(hasStopLossLevelReachedNotification) {
		this.data.hasStopLossLevelReachedNotification = hasStopLossLevelReachedNotification;

		this.dispatchCustomEvent(watchlistEvents.ITEM_STOPLOSS_ALERT, this.data);

		this.requestUpdate();
	}

	addAlert() {
		const {
			data: { id: portfolioItemId, isin },
			bid,
		} = this;

		this.dispatchCustomEvent(alertEvents.EDIT, {
			portfolioItemId,
			isin,
			threshold: bid,
		});
	}

	addToPortfolio() {
		this.createDialog(this.data);
	}

	createDialog(portfolioItem) {
		const tagName = 'x-portfolio-item-edit-form';
		const { ask: purchasePrice, isin: title } = portfolioItem;
		const options = { ...portfolioItem, amount: 1, purchasePrice };

		constructDialog({ title }, tagName, { options: JSON.stringify(options) });
	}

	dispatchCustomEvent(eventName, detail = {}) {
		const event = new CustomEvent(eventName, {
			bubbles: true,
			detail,
		});

		this.dispatchEvent(event);
	}

	createRenderRoot() {
		return this;
	}
}

customElements.define('x-watchlist-item', WatchlistItem);

export default WatchlistItem;
